import { useEffect, useRef } from "react";
import { useAppSelector } from "../store/hooks";

/**
 * Локалният резерв за компонентите (docs/edge.md). Две тесни селекции вместо
 * една обвивка-обект, за да са стабилни като зависимости на ефекти.
 */

/** Терминалът работи срещу касата (без интернет). */
export const useIsEdgeLocal = (): boolean =>
  useAppSelector((state) => state.edge.mode === "local");

/**
 * Локален режим БЕЗ билет (§14.3.1 на договора): през касата минават само
 * `POST {edge}/user/login` и `POST {edge}/user/login-with-code`, затова
 * терминалът стои на екрана за вход (ProtectedRoute / SharedAccountGate).
 */
export const useIsEdgeRestricted = (): boolean =>
  useAppSelector(
    (state) => state.edge.mode === "local" && state.edge.restricted,
  );

/** Цялото състояние на резерва — за лентата и за реда в Устройства. */
export const useEdgeState = () => useAppSelector((state) => state.edge);

/**
 * „Резервации" и „Настани" са заключени: терминалът е срещу касата, а тя НЕ
 * умее резервации (по-стара каса или облак без миграцията —
 * `edgeSlice.reservationsOffline`). Каса, която ги умее, ги сервира и без
 * интернет (docs/edge.md §Резервации без интернет).
 */
export const reservationsLockedFor = (edge: {
  mode: string;
  reservationsOffline?: boolean;
}): boolean => edge.mode === "local" && edge.reservationsOffline !== true;

export const useReservationsLocked = (): boolean =>
  useAppSelector((state) => reservationsLockedFor(state.edge));

/**
 * Бакшишът в „Закрий" е заключен: терминалът е срещу касата, а тя НЕ го
 * умее (`edgeSlice.tipsOffline`, `capabilities.tips` в `GET {edge}/health`).
 * По-стара каса пренебрегва непознатите полета и би закрила без бакшиша —
 * затова секцията е заключена с „Недостъпно без интернет", не скрита.
 */
export const tipsLockedFor = (edge: {
  mode: string;
  tipsOffline?: boolean;
}): boolean => edge.mode === "local" && edge.tipsOffline !== true;

export const useTipsLocked = (): boolean =>
  useAppSelector((state) => tipsLockedFor(state.edge));

/**
 * Отстъпката в € на цялата сметка е заключена: терминалът е срещу касата, а тя
 * НЕ я умее (`edgeSlice.billDiscountOffline`, `capabilities.bill_discount`).
 * Тогава табът „На цялата сметка" и „Закрий" за сметка с такава отстъпка са
 * заключени, а редът ѝ в кутийката на масата го няма — по-стара каса би
 * закрила по пълни цени.
 */
export const billDiscountLockedFor = (edge: {
  mode: string;
  billDiscountOffline?: boolean;
}): boolean => edge.mode === "local" && edge.billDiscountOffline !== true;

export const useBillDiscountLocked = (): boolean =>
  useAppSelector((state) => billDiscountLockedFor(state.edge));

/**
 * Вика `onChange` при всяка СМЯНА на режима (облак ↔ каса), не при първия
 * рендер. За екраните, които държат избрано ид (резервация, маса, стъпка на
 * модал): данните от другата страна може да го нямат, а ид-тата на касата
 * (≥ 1 000 000 000) облакът превежда сам.
 */
export const useOnEdgeModeChange = (onChange: () => void): void => {
  const mode = useAppSelector((state) => state.edge.mode);
  const previousMode = useRef(mode);
  const callback = useRef(onChange);
  callback.current = onChange;
  useEffect(() => {
    if (previousMode.current === mode) return;
    previousMode.current = mode;
    callback.current();
  }, [mode]);
};

/**
 * Като `useOnEdgeModeChange`, но само когато касата сервира резервации
 * (`reservationsOffline`) — тогава другата страна наистина дава други данни.
 * Срещу каса без резервации модулът просто се заключва и избраното (вкл.
 * започната редакция) остава за връщането в облака, както досега.
 */
export const useOnReservationsSourceChange = (onChange: () => void): void => {
  const tillServes = useAppSelector((state) => state.edge.reservationsOffline === true);
  useOnEdgeModeChange(() => {
    if (tillServes) onChange();
  });
};
